<template>
  <section class="speaker-control-panel" :class="{ 'speaker-control-panel--disabled': !canOperate && !canStopOperate }">
    <header class="speaker-control-panel__header">
      <div>
        <strong><SoundOutlined />喊话器</strong>
        <small>{{ deviceSummary }}</small>
      </div>
      <span class="speaker-control-panel__state" :class="`speaker-control-panel__state--${stateTone}`">{{ stateText }}</span>
    </header>

    <p v-if="unavailableReason" class="speaker-control-panel__notice">
      <InfoCircleOutlined />{{ unavailableReason }}
    </p>

    <nav class="speaker-control-panel__modes" role="tablist" aria-label="喊话方式">
      <button
        v-for="mode in modeOptions"
        :key="mode.value"
        type="button"
        role="tab"
        :aria-selected="speakerMode === mode.value"
        :class="{ 'speaker-control-panel__mode--active': speakerMode === mode.value }"
        :disabled="(recording || liveActive) && speakerMode !== mode.value"
        :title="mode.hint"
        @click="speakerMode = mode.value"
      >
        <component :is="mode.icon" />
        <span>{{ mode.label }}</span>
      </button>
    </nav>

    <input
      ref="fileInputRef"
      class="speaker-control-panel__file"
      type="file"
      accept="audio/mpeg,audio/wav,audio/x-wav,audio/mp4,audio/aac,.mp3,.wav,.m4a,.aac"
      @change="handleAudioFile"
    />

    <div class="speaker-control-panel__actions" :class="{ 'speaker-control-panel__actions--four': recording }">
      <template v-if="speakerMode === 'file'">
        <a-button
          size="small"
          type="primary"
          class="speaker-control-panel__primary"
          :disabled="!canOperate || busy"
          :loading="fileLoading"
          title="选择 MP3 / WAV / M4A / AAC 文件，浏览器转码为 PCM 后上传播放，单次不超过 3 分钟"
          @click="openFilePicker"
        >
          <FolderOpenOutlined />播放音频文件
        </a-button>
      </template>
      <template v-else-if="speakerMode === 'record'">
        <a-button
          v-if="!recording"
          size="small"
          type="primary"
          class="speaker-control-panel__primary"
          :disabled="!canOperate || busy"
          title="先录一段话，停止后自动上传播放；最长 3 分钟"
          @click="startRecording"
        >
          <AudioOutlined />录音喊话
        </a-button>
        <template v-else>
          <a-button size="small" type="primary" danger :disabled="recordingStopping" @click="cancelRecording">
            取消录音
          </a-button>
          <a-button size="small" type="primary" :loading="recordingStopping" @click="stopRecordingAndPlay">
            停止并播放
          </a-button>
        </template>
      </template>
      <template v-else>
        <button
          type="button"
          class="speaker-live-toggle speaker-control-panel__primary"
          :class="{ 'speaker-live-toggle--active': liveActive }"
          :disabled="(!canOperate && !liveActive) || liveStarting || fileLoading || recording"
          :aria-pressed="liveActive"
          title="麦克风语音按 2 秒左右分段经云端转发到喊话器，约 3~6 秒延迟；说完后点击结束"
          @click="toggleLive"
        >
          <AudioOutlined />
          <span>{{ liveStarting ? '打开麦克风…' : (liveActive ? '结束实时喊话' : '开始实时喊话') }}</span>
        </button>
      </template>
      <a-button size="small" :disabled="!canStopOperate || commandLoading || !canStop" :loading="stopLoading" title="停止喊话器播放" @click="stopPlayback">
        <StopOutlined />停止
      </a-button>
      <a-button size="small" :disabled="!canOperate || commandLoading || !canReplay || liveActive" :loading="replayLoading" title="重播最近一次音频" @click="replayPlayback">
        <RedoOutlined />重播
      </a-button>
    </div>

    <div v-if="speakerMode === 'live'" class="speaker-control-panel__live-line">
      <div class="speaker-live-meter" :class="{ 'speaker-live-meter--active': liveActive }" aria-hidden="true">
        <span v-for="bar in 14" :key="bar" :class="{ 'speaker-live-meter__bar--on': liveLevelBars >= bar }"></span>
      </div>
      <span class="speaker-live-status" :class="{ 'speaker-live-status--active': liveActive }">{{ liveStatusText }}</span>
    </div>

    <div v-if="recording" class="speaker-control-panel__recording">
      <span class="speaker-control-panel__recording-dot"></span>
      正在录音 {{ recordingTimeText }}，最长 03:00
    </div>

    <div class="speaker-control-panel__settings">
      <label class="speaker-control-panel__setting">
        <span>播放模式</span>
        <a-select
          v-model:value="settings.playMode"
          size="small"
          :options="playModeOptions"
          :disabled="!canOperate || settingsLoading"
          dropdown-class-name="agora-inline-dropdown"
          @change="savePlayMode"
        />
      </label>
      <div class="speaker-control-panel__volume" role="radiogroup" :aria-label="`音量 ${settings.playVolume}%`">
        <span class="speaker-control-panel__volume-label">音量 {{ settings.playVolume }}%</span>
        <button
          v-for="level in volumeLevels"
          :key="level"
          type="button"
          role="radio"
          :aria-checked="activeVolumeLevel === level"
          :class="{ 'speaker-control-panel__volume-level--active': activeVolumeLevel === level }"
          :disabled="!canOperate || settingsLoading"
          :title="`音量 ${level}%`"
          @click="selectVolumeLevel(level)"
        >{{ level }}</button>
      </div>
    </div>

    <div v-if="operationVisible || speakerState?.play_file_name" class="speaker-control-panel__status-line">
      <span class="speaker-control-panel__status-text" :title="statusLineText">{{ statusLineText }}</span>
      <span v-if="operationVisible" class="speaker-control-panel__status-percent">{{ operationPercent }}%</span>
    </div>
    <a-progress
      v-if="operationVisible"
      class="speaker-control-panel__bar"
      :percent="operationPercent"
      :show-info="false"
      :stroke-width="3"
      size="small"
      :status="operationProgressStatus"
    />
  </section>
</template>

<script lang="ts" setup>
import { computed, defineEmits, defineProps, onBeforeUnmount, onMounted, reactive, ref, watch } from 'vue'
import { message } from 'ant-design-vue'
import {
  AudioOutlined,
  FolderOpenOutlined,
  InfoCircleOutlined,
  RedoOutlined,
  SoundOutlined,
  StopOutlined,
} from '@ant-design/icons-vue'
import {
  getSpeakerStatus,
  playSpeakerAudio,
  replaySpeakerAudio,
  stopSpeakerAudio,
  updateSpeakerSettings,
} from '/@/api/speaker'
import {
  LiveSpeakerBuffer,
  buildLiveSegment,
  describeLiveStatus,
  hasVoice,
  livePlaybackTimeoutMs,
  measurePeakLevel,
  resolveLiveFlush,
} from '/@/utils/speaker-live'
import { startLiveMicrophoneCapture } from '/@/utils/speaker-live-capture'
import type { LiveMicrophoneCapture } from '/@/utils/speaker-live-capture'
import { SpeakerOperation, SpeakerStatusResponse } from '/@/types/speaker'
import { convertSpeakerAudioToPcm, SPEAKER_MAX_DURATION_MS } from '/@/utils/speaker-audio'
import {
  findSpeakerState,
  normalizeSpeakerOperation,
  speakerStateFromStatus,
} from '/@/utils/speaker-state'
import EventBus from '/@/event-bus'
import { DRC_METHOD } from '/@/types/drc'

const props = defineProps<{
  dockSn: string
  source?: unknown
  enabled?: boolean
  stopEnabled?: boolean
  disabledReason?: string
  useDrcHeaders?: boolean
}>()
const emit = defineEmits(['state-change'])

const fileInputRef = ref<HTMLInputElement>()
const remoteStatus = ref<SpeakerStatusResponse | null>(null)
const pushedStatus = ref<SpeakerStatusResponse | null>(null)
const drcSpeakerSource = ref<unknown>(null)
const operation = ref<SpeakerOperation | null>(null)
const statusError = ref('')
const fileLoading = ref(false)
const settingsLoading = ref(false)
const stopLoading = ref(false)
const replayLoading = ref(false)
const recording = ref(false)
const recordingStopping = ref(false)
const recordingElapsedMs = ref(0)
const settings = reactive({
  playMode: 0,
  playVolume: 70,
})
type SpeakerPanelMode = 'file' | 'record' | 'live'
const speakerMode = ref<SpeakerPanelMode>('file')
const modeOptions: Array<{ value: SpeakerPanelMode; label: string; hint: string; icon: unknown }> = [
  { value: 'file', label: '音频文件', hint: '上传 MP3 / WAV / M4A / AAC 文件播放', icon: FolderOpenOutlined },
  { value: 'record', label: '录音喊话', hint: '录一段话，停止后上传播放', icon: AudioOutlined },
  { value: 'live', label: '实时喊话', hint: '麦克风语音分段实时转发', icon: SoundOutlined },
]
const liveActive = ref(false)
const liveStarting = ref(false)
const liveLevel = ref(0)
const liveSentSegments = ref(0)
const liveBufferedMs = ref(0)
const liveAwaiting = ref(false)
let liveSegmentSentAt = 0
let liveSegmentMs = 0
const playModeOptions = [
  { label: '单次播放', value: 0 },
  { label: '循环播放', value: 1 },
]
/** One-click volume presets; the label still shows the exact reported value. */
const volumeLevels = Array.from({ length: 10 }, (_, index) => (index + 1) * 10)

let statusRefreshTimer: number | undefined
let recordingTimer: number | undefined
let liveCapture: LiveMicrophoneCapture | null = null
let liveBuffer = new LiveSpeakerBuffer()
let liveTimer: number | undefined
let liveAwaitTimer: number | undefined
let liveFlushRunning = false
let liveSegmentIndex = 0
let mediaRecorder: MediaRecorder | null = null
let mediaStream: MediaStream | null = null
let recordingChunks: Blob[] = []
let recordingStartedAt = 0
let disposed = false
let lastEmittedSpeakerState = ''

const speakerState = computed(() => findSpeakerState(
  drcSpeakerSource.value,
  speakerStateFromStatus(pushedStatus.value),
  props.source,
  speakerStateFromStatus(remoteStatus.value),
))
const psdkIndex = computed(() => speakerState.value?.psdk_index ?? '')
const commandPsdkIndex = computed(() => {
  const value = Number(psdkIndex.value)
  return Number.isInteger(value) && value >= 0 && value <= 3 ? value : null
})
const canOperate = computed(() => props.enabled !== false && !!props.dockSn && commandPsdkIndex.value !== null)
const canStopOperate = computed(() => (
  (props.stopEnabled ?? props.enabled) !== false &&
  !!props.dockSn &&
  commandPsdkIndex.value !== null
))
const commandLoading = computed(() => fileLoading.value || settingsLoading.value || stopLoading.value || replayLoading.value)
const deviceBusy = computed(() => isPlayingSystemState(speakerState.value?.system_state))
// DJI reports progress "ok" when playback *starts*, so our operation record is
// terminal long before the speaker goes quiet; the reported system_state is
// the only reliable "still playing" signal and gates every new playback.
const busy = computed(() => commandLoading.value || isOperationActive(operation.value) || deviceBusy.value)
const canStop = computed(() => isOperationActive(operation.value) || deviceBusy.value)
const canReplay = computed(() => !!speakerState.value?.play_file_name || !!speakerState.value?.play_file_md5 || !!operation.value?.md5)
const unavailableReason = computed(() => {
  if (!props.dockSn) {
    return '缺少机场 SN，无法读取喊话器状态'
  }
  if (!speakerState.value) {
    return statusError.value || '未检测到 AS1 喊话器，等待设备状态上报'
  }
  if (props.enabled === false) {
    const reason = props.disabledReason || '当前控制链路未就绪'
    return canStopOperate.value && canStop.value ? `${reason}；当前播放仍可手动停止` : reason
  }
  return ''
})
const deviceSummary = computed(() => {
  if (!speakerState.value) {
    return 'AS1 · 等待状态'
  }
  return `${speakerState.value.psdk_name || 'Speaker'} · ${psdkIndex.value}`
})
const stateText = computed(() => {
  if (recording.value) {
    return '本地录音中'
  }
  const operationStatus = String(operation.value?.status || '').toUpperCase()
  if (operationStatus && !isTerminalStatus(operationStatus)) {
    return operationStatus === 'WAITING' ? '等待播放' : '处理中'
  }
  if (operationStatus === 'FAILED') {
    return '播放失败'
  }
  if (!speakerState.value) {
    return '未检测到'
  }
  const labels: Record<number, string> = {
    0: '空闲',
    1: '传输中',
    2: '播放中',
    3: '异常',
    4: 'TTS 转换中',
    99: '下载中',
  }
  return labels[Number(speakerState.value.system_state)] || '已连接'
})
const stateTone = computed(() => {
  if (!speakerState.value) return 'muted'
  if (recording.value || isOperationActive(operation.value) || isPlayingSystemState(speakerState.value.system_state)) return 'active'
  if (props.enabled === false) return 'muted'
  if (String(operation.value?.status || '').toUpperCase() === 'FAILED' || Number(speakerState.value.system_state) === 3) return 'danger'
  return 'ready'
})
const recordingTimeText = computed(() => formatClock(recordingElapsedMs.value))
const activeVolumeLevel = computed(() => {
  const rounded = Math.round(settings.playVolume / 10) * 10
  return rounded >= 10 ? Math.min(100, rounded) : null
})
const liveLevelBars = computed(() => Math.round(Math.min(1, liveLevel.value * 2.5) * 14))
const liveStatusText = computed(() => describeLiveStatus({
  active: liveActive.value,
  starting: liveStarting.value,
  awaitingPlayback: liveAwaiting.value,
  bufferedMs: liveBufferedMs.value,
  sentSegments: liveSentSegments.value,
}))
const operationPercent = computed(() => Math.round(Math.max(0, Math.min(100, Number(operation.value?.percent) || 0))))
const operationVisible = computed(() => !!operation.value && (!!operation.value.status || operationPercent.value > 0))
const operationText = computed(() => {
  const status = String(operation.value?.status || '').toUpperCase()
  if (status === 'SUCCESS' || status === 'OK') return '播放完成'
  if (status === 'FAILED') return '播放失败'
  if (status === 'CANCELED' || status === 'CANCELLED') return '播放已停止'
  if (operation.value?.step_key) return operation.value.step_key
  if (status === 'WAITING') return '等待设备接收'
  return '正在传输并播放'
})
const statusLineText = computed(() => {
  const fileName = speakerState.value?.play_file_name || operation.value?.md5 || ''
  if (operationVisible.value) {
    return fileName ? `${operationText.value} · ${fileName}` : operationText.value
  }
  return `最近播放：${fileName}`
})
const operationProgressStatus = computed<'active' | 'success' | 'exception' | 'normal'>(() => {
  const status = String(operation.value?.status || '').toUpperCase()
  if (status === 'SUCCESS' || status === 'OK') return 'success'
  if (status === 'FAILED') return 'exception'
  return isTerminalStatus(status) ? 'normal' : 'active'
})

watch(speakerState, value => {
  if (value?.play_mode !== undefined && !settingsLoading.value) {
    settings.playMode = Number(value.play_mode)
  }
  if (value?.play_volume !== undefined && !settingsLoading.value) {
    settings.playVolume = Math.max(0, Math.min(100, Number(value.play_volume)))
  }
  const signature = JSON.stringify(value) || 'null'
  if (signature !== lastEmittedSpeakerState) {
    lastEmittedSpeakerState = signature
    emit('state-change', value ? { ...value } : null)
  }
}, { immediate: true, deep: true })

watch(() => props.dockSn, () => {
  lastEmittedSpeakerState = ''
  emit('state-change', null)
  remoteStatus.value = null
  pushedStatus.value = null
  drcSpeakerSource.value = null
  operation.value = null
  scheduleStatusRefresh(true)
})

watch(() => props.enabled, enabled => {
  if (enabled === false && recording.value) {
    cancelRecording()
  }
  if (enabled === false && liveActive.value) {
    stopLive(false)
    message.warning('控制链路或负载权已失效，实时喊话已结束')
  }
})

function formatClock (milliseconds: number) {
  const seconds = Math.max(0, Math.floor(milliseconds / 1000))
  return `${String(Math.floor(seconds / 60)).padStart(2, '0')}:${String(seconds % 60).padStart(2, '0')}`
}

function isTerminalStatus (status: string) {
  return ['SUCCESS', 'OK', 'FAILED', 'CANCELED', 'CANCELLED', 'STOPPED'].includes(status)
}

function isOperationActive (value?: SpeakerOperation | null) {
  const status = String(value?.status || '').toUpperCase()
  return !!status && !isTerminalStatus(status)
}

function isPlayingSystemState (systemState: unknown) {
  return [1, 2, 4, 99].includes(Number(systemState))
}

function errorText (error: any, fallback: string) {
  return String(error?.response?.data?.message || error?.message || fallback)
}

function scheduleStatusRefresh (immediate = false) {
  if (disposed) {
    return
  }
  if (statusRefreshTimer !== undefined) {
    window.clearTimeout(statusRefreshTimer)
  }
  statusRefreshTimer = window.setTimeout(refreshStatus, immediate ? 0 : 5000)
}

async function refreshStatus () {
  if (!props.dockSn) {
    return
  }
  try {
    const result = await getSpeakerStatus(props.dockSn, props.useDrcHeaders === true)
    if (result.code === 0) {
      remoteStatus.value = result.data
      if (!result.data?.speakers?.length) {
        // The poll is the same snapshot the push is built from, so an empty
        // list here retires an earlier pushed speaker instead of leaving it
        // visible forever after the aircraft goes offline.
        pushedStatus.value = null
      }
      statusError.value = ''
      const nextOperation = normalizeSpeakerOperation(result.data?.operation)
      if (nextOperation) {
        operation.value = nextOperation
      }
    } else {
      statusError.value = result.message || '喊话器状态暂不可用'
    }
  } catch (_error) {
    statusError.value = '喊话器状态接口暂不可用'
  } finally {
    if (!disposed) {
      scheduleStatusRefresh()
    }
  }
}

function openFilePicker () {
  fileInputRef.value?.click()
}

async function handleAudioFile (event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  input.value = ''
  if (!file) {
    return
  }
  await convertAndPlay(file, file.name, 'FILE')
}

async function convertAndPlay (source: Blob, fileName: string, sourceType: 'FILE' | 'MICROPHONE') {
  if (!canOperate.value || commandPsdkIndex.value === null) {
    message.warning(unavailableReason.value || '喊话器暂不可用')
    return
  }
  fileLoading.value = true
  try {
    const converted = await convertSpeakerAudioToPcm(source, fileName, sourceType === 'MICROPHONE')
    const currentCommandPsdkIndex = commandPsdkIndex.value
    if (!canOperate.value || currentCommandPsdkIndex === null) {
      throw new Error('控制链路或喊话器负载权已失效，请重新获取后再播放')
    }
    const result = await playSpeakerAudio(props.dockSn, {
      psdkIndex: currentCommandPsdkIndex,
      fileName: converted.fileName,
      source: sourceType,
      durationMs: converted.durationMs,
      file: converted.blob,
    }, props.useDrcHeaders === true)
    if (result.code !== 0) {
      throw new Error(result.message || '喊话指令发送失败')
    }
    operation.value = normalizeSpeakerOperation(result.data) || {
      psdk_index: currentCommandPsdkIndex,
      status: 'WAITING',
      percent: 0,
    }
    message.success(sourceType === 'MICROPHONE' ? '录音已上传，等待喊话器播放' : '音频已上传，等待喊话器播放')
    scheduleStatusRefresh(true)
  } catch (error) {
    message.error(errorText(error, '音频处理或播放失败'))
  } finally {
    fileLoading.value = false
  }
}

function preferredRecorderMimeType () {
  const candidates = ['audio/webm;codecs=opus', 'audio/mp4', 'audio/webm']
  return candidates.find(type => typeof MediaRecorder.isTypeSupported !== 'function' || MediaRecorder.isTypeSupported(type)) || ''
}

async function startRecording () {
  if (!canOperate.value) {
    message.warning(unavailableReason.value || '喊话器暂不可用')
    return
  }
  if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === 'undefined') {
    message.error('当前浏览器不支持麦克风录音')
    return
  }
  if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    message.error('麦克风录音需要通过 HTTPS 打开页面')
    return
  }
  try {
    mediaStream = await navigator.mediaDevices.getUserMedia({
      audio: {
        channelCount: 1,
        echoCancellation: true,
        noiseSuppression: true,
      },
    })
    const mimeType = preferredRecorderMimeType()
    mediaRecorder = mimeType ? new MediaRecorder(mediaStream, { mimeType }) : new MediaRecorder(mediaStream)
    recordingChunks = []
    mediaRecorder.addEventListener('dataavailable', event => {
      if (event.data.size > 0) {
        recordingChunks.push(event.data)
      }
    })
    mediaRecorder.start(1000)
    recordingStartedAt = Date.now()
    recordingElapsedMs.value = 0
    recording.value = true
    recordingTimer = window.setInterval(() => {
      recordingElapsedMs.value = Date.now() - recordingStartedAt
      if (recordingElapsedMs.value >= SPEAKER_MAX_DURATION_MS) {
        cancelRecording()
        message.warning('录音已达到 3 分钟上限，请缩短内容后重试')
      }
    }, 250)
  } catch (error: any) {
    stopMediaStream()
    message.error(error?.name === 'NotAllowedError' ? '麦克风权限被拒绝，请在浏览器设置中允许访问' : '无法打开麦克风，请检查设备和浏览器权限')
  }
}

function stopMediaStream () {
  mediaStream?.getTracks().forEach(track => track.stop())
  mediaStream = null
  if (recordingTimer !== undefined) {
    window.clearInterval(recordingTimer)
    recordingTimer = undefined
  }
}

async function finishRecording (shouldPlay: boolean) {
  if (!mediaRecorder || !recording.value || recordingStopping.value) {
    return
  }
  recordingStopping.value = true
  const recorder = mediaRecorder
  try {
    if (recorder.state !== 'inactive') {
      const stopped = new Promise<void>(resolve => recorder.addEventListener('stop', () => resolve(), { once: true }))
      recorder.stop()
      await stopped
    }
    const durationMs = Math.max(1, Date.now() - recordingStartedAt)
    const blob = new Blob(recordingChunks, { type: recorder.mimeType || 'audio/webm' })
    recording.value = false
    stopMediaStream()
    if (shouldPlay && blob.size > 0) {
      const suffix = recorder.mimeType.includes('mp4') ? 'm4a' : 'webm'
      await convertAndPlay(blob, `microphone-${Date.now()}.${suffix}`, 'MICROPHONE')
    } else if (shouldPlay) {
      message.error('未录制到有效音频')
    }
    recordingElapsedMs.value = shouldPlay ? durationMs : 0
  } finally {
    mediaRecorder = null
    recordingChunks = []
    recording.value = false
    recordingStopping.value = false
    stopMediaStream()
  }
}

async function cancelRecording () {
  await finishRecording(false)
}

async function stopRecordingAndPlay () {
  await finishRecording(true)
}

/* ---------------- live (push-to-talk) announcements ---------------- */

function sleep (milliseconds: number) {
  return new Promise<void>(resolve => window.setTimeout(resolve, milliseconds))
}

function releaseLiveAwait () {
  if (liveAwaitTimer !== undefined) {
    window.clearTimeout(liveAwaitTimer)
    liveAwaitTimer = undefined
  }
  liveAwaiting.value = false
}

function armLiveAwait (segmentMs: number) {
  releaseLiveAwait()
  liveAwaiting.value = true
  liveSegmentSentAt = Date.now()
  liveSegmentMs = segmentMs
  // A lost state push must not freeze the relay; assume the segment ended.
  liveAwaitTimer = window.setTimeout(() => {
    liveAwaitTimer = undefined
    liveAwaiting.value = false
  }, livePlaybackTimeoutMs(segmentMs))
}

/**
 * The previous segment counts as finished once it has had at least its own
 * duration to play and the speaker no longer reports a busy system_state.
 * (The operation record alone is not enough: DJI marks it ok at play start.)
 */
function settleLiveAwait () {
  if (!liveAwaiting.value) {
    return
  }
  const elapsed = Date.now() - liveSegmentSentAt
  if (elapsed >= liveSegmentMs + 300 && !deviceBusy.value) {
    releaseLiveAwait()
  }
}

async function toggleLive () {
  if (liveActive.value) {
    await stopLive(true)
  } else {
    await startLive()
  }
}

async function startLive () {
  if (liveActive.value || liveStarting.value) {
    return
  }
  if (!canOperate.value) {
    message.warning(unavailableReason.value || '喊话器暂不可用')
    return
  }
  if (!window.isSecureContext && !['localhost', '127.0.0.1'].includes(window.location.hostname)) {
    message.error('麦克风采集需要通过 HTTPS 打开页面')
    return
  }
  if (recording.value) {
    await cancelRecording()
  }
  liveStarting.value = true
  try {
    liveBuffer = new LiveSpeakerBuffer()
    liveSegmentIndex = 0
    liveSentSegments.value = 0
    liveBufferedMs.value = 0
    releaseLiveAwait()
    liveCapture = await startLiveMicrophoneCapture(chunk => {
      liveBuffer.push(chunk)
      liveLevel.value = measurePeakLevel(chunk)
      liveBufferedMs.value = liveBuffer.durationMs
    })
    liveBuffer = new LiveSpeakerBuffer(liveCapture.sampleRate)
    liveActive.value = true
    liveTimer = window.setInterval(() => {
      settleLiveAwait()
      runLiveFlush(false)
    }, 250)
    message.success('实时喊话已开始，请对着麦克风说话')
  } catch (error: any) {
    await liveCapture?.stop().catch(() => undefined)
    liveCapture = null
    message.error(error?.name === 'NotAllowedError'
      ? '麦克风权限被拒绝，请在浏览器设置中允许访问'
      : errorText(error, '无法打开麦克风，请检查设备和浏览器权限'))
  } finally {
    liveStarting.value = false
  }
}

async function stopLive (flushTail: boolean) {
  if (!liveActive.value && !liveCapture) {
    return
  }
  if (liveTimer !== undefined) {
    window.clearInterval(liveTimer)
    liveTimer = undefined
  }
  const capture = liveCapture
  liveCapture = null
  await capture?.stop().catch(() => undefined)
  liveActive.value = false
  liveLevel.value = 0
  if (flushTail) {
    await runLiveFlush(true)
  } else {
    liveBuffer.clear()
    releaseLiveAwait()
  }
  liveBufferedMs.value = liveBuffer.durationMs
}

async function runLiveFlush (finishing: boolean) {
  if (liveFlushRunning) {
    return
  }
  liveFlushRunning = true
  try {
    for (;;) {
      const decision = resolveLiveFlush({
        bufferedMs: liveBuffer.durationMs,
        awaitingPlayback: liveAwaiting.value,
        finishing,
      })
      if (decision.action === 'trim') {
        const dropped = liveBuffer.trimTo(decision.keepMs)
        if (dropped > 0) {
          message.warning(`喊话器或网络较慢，已丢弃 ${Math.round(dropped / 1000)} 秒积压语音`)
        }
        continue
      }
      if (decision.action !== 'send') {
        break
      }
      const samples = liveBuffer.take(decision.segmentMs)
      liveBufferedMs.value = liveBuffer.durationMs
      if (!hasVoice(samples)) {
        // Silence between sentences is not worth a round trip to the aircraft.
        if (finishing) {
          break
        }
        continue
      }
      await sendLiveSegment(samples)
      break
    }
  } finally {
    liveFlushRunning = false
    liveBufferedMs.value = liveBuffer.durationMs
  }
}

async function sendLiveSegment (samples: Float32Array) {
  const currentCommandPsdkIndex = commandPsdkIndex.value
  if (!canOperate.value || currentCommandPsdkIndex === null) {
    await stopLive(false)
    message.warning(unavailableReason.value || '喊话器负载权已失效，实时喊话已结束')
    return
  }
  liveSegmentIndex += 1
  const segment = buildLiveSegment(samples, liveBuffer.sampleRate, liveSegmentIndex)
  armLiveAwait(segment.durationMs)
  const retryUntil = Date.now() + livePlaybackTimeoutMs(liveSegmentMs)
  for (let attempt = 1; ; attempt += 1) {
    try {
      const result = await playSpeakerAudio(props.dockSn, {
        psdkIndex: currentCommandPsdkIndex,
        fileName: segment.fileName,
        source: 'MICROPHONE',
        durationMs: segment.durationMs,
        file: segment.blob,
      }, props.useDrcHeaders === true, { skipErrorMessage: true })
      if (result.code !== 0) {
        throw new Error(result.message || '喊话指令发送失败')
      }
      operation.value = normalizeSpeakerOperation(result.data) || {
        psdk_index: currentCommandPsdkIndex,
        status: 'WAITING',
        percent: 0,
      }
      liveSentSegments.value += 1
      return
    } catch (error: any) {
      const status = Number(error?.response?.status)
      // 409: the speaker is still playing the previous segment; keep the
      // audio and try again shortly instead of dropping what was said.
      if (status === 409 && liveActive.value && Date.now() < retryUntil) {
        await sleep(800)
        continue
      }
      releaseLiveAwait()
      message.error(errorText(error, '实时喊话分段发送失败'))
      if (status !== 409) {
        await stopLive(false)
      }
      return
    }
  }
}

async function savePlayMode () {
  await saveSettings({ play_mode: settings.playMode })
}

async function selectVolumeLevel (level: number) {
  if (level === settings.playVolume || settingsLoading.value) {
    return
  }
  const previous = settings.playVolume
  settings.playVolume = level
  const saved = await saveSettings({ play_volume: level })
  if (!saved) {
    settings.playVolume = previous
  }
}

async function saveSettings (value: { play_mode?: number, play_volume?: number }) {
  const currentCommandPsdkIndex = commandPsdkIndex.value
  if (!canOperate.value || currentCommandPsdkIndex === null) {
    return false
  }
  settingsLoading.value = true
  try {
    const result = await updateSpeakerSettings(props.dockSn, {
      psdk_index: currentCommandPsdkIndex,
      ...value,
    }, props.useDrcHeaders === true)
    if (result.code !== 0) {
      throw new Error(result.message || '喊话器设置失败')
    }
    message.success('喊话器设置已更新')
    scheduleStatusRefresh(true)
    return true
  } catch (error) {
    message.error(errorText(error, '喊话器设置失败'))
    return false
  } finally {
    settingsLoading.value = false
  }
}

async function stopPlayback () {
  const currentCommandPsdkIndex = commandPsdkIndex.value
  if (!canStopOperate.value || currentCommandPsdkIndex === null) {
    return
  }
  if (liveActive.value) {
    await stopLive(false)
  }
  stopLoading.value = true
  try {
    const result = await stopSpeakerAudio(props.dockSn, { psdk_index: currentCommandPsdkIndex }, props.useDrcHeaders === true)
    if (result.code !== 0) {
      throw new Error(result.message || '停止喊话失败')
    }
    operation.value = normalizeSpeakerOperation(result.data) || { psdk_index: currentCommandPsdkIndex, status: 'STOPPED', percent: operationPercent.value }
    message.success('已发送停止喊话指令')
    scheduleStatusRefresh(true)
  } catch (error) {
    message.error(errorText(error, '停止喊话失败'))
  } finally {
    stopLoading.value = false
  }
}

async function replayPlayback () {
  const currentCommandPsdkIndex = commandPsdkIndex.value
  if (!canOperate.value || currentCommandPsdkIndex === null) {
    return
  }
  replayLoading.value = true
  try {
    const result = await replaySpeakerAudio(props.dockSn, { psdk_index: currentCommandPsdkIndex }, props.useDrcHeaders === true)
    if (result.code !== 0) {
      throw new Error(result.message || '重播喊话失败')
    }
    operation.value = normalizeSpeakerOperation(result.data) || { psdk_index: currentCommandPsdkIndex, status: 'WAITING', percent: 0 }
    message.success('已发送重播指令')
    scheduleStatusRefresh(true)
  } catch (error) {
    message.error(errorText(error, '重播喊话失败'))
  } finally {
    replayLoading.value = false
  }
}

function handleSpeakerProgress (payload: any) {
  const next = normalizeSpeakerOperation(payload)
  if (!next) {
    return
  }
  if (next.dock_sn && next.dock_sn !== props.dockSn) {
    return
  }
  if (next.psdk_index !== undefined && next.psdk_index !== null && psdkIndex.value !== '' && String(next.psdk_index) !== String(psdkIndex.value)) {
    return
  }
  if (operation.value?.operation_id && next.operation_id && operation.value.operation_id !== next.operation_id) {
    return
  }
  operation.value = { ...(operation.value || {}), ...next }
  if (next.status && isTerminalStatus(String(next.status).toUpperCase())) {
    scheduleStatusRefresh(true)
  }
}

function handleSpeakerStateNotify (payload: any) {
  const dockSn = String(payload?.dock_sn ?? payload?.dockSn ?? '')
  if (!Array.isArray(payload?.speakers) || (dockSn && dockSn !== props.dockSn)) {
    return
  }
  pushedStatus.value = payload as SpeakerStatusResponse
}

function handleDrcSpeakerInfo (payload: any) {
  if (payload?.method === DRC_METHOD.DRC_PSDK_STATE_INFO) {
    drcSpeakerSource.value = payload.data
  }
  if (payload?.method === DRC_METHOD.DRC_SPEAKER_PLAY_PROGRESS) {
    handleSpeakerProgress(payload.data)
  }
}

onMounted(() => {
  disposed = false
  EventBus.on('speakerPlayProgress', handleSpeakerProgress)
  EventBus.on('speakerState', handleSpeakerStateNotify)
  EventBus.on('droneControlMqttInfo', handleDrcSpeakerInfo)
  scheduleStatusRefresh(true)
})

onBeforeUnmount(() => {
  disposed = true
  EventBus.off('speakerPlayProgress', handleSpeakerProgress)
  EventBus.off('speakerState', handleSpeakerStateNotify)
  EventBus.off('droneControlMqttInfo', handleDrcSpeakerInfo)
  if (statusRefreshTimer !== undefined) {
    window.clearTimeout(statusRefreshTimer)
  }
  if (recording.value) {
    cancelRecording()
  } else {
    stopMediaStream()
  }
  if (liveActive.value || liveCapture) {
    stopLive(false)
  }
})
</script>

<style lang="scss" scoped>
.speaker-control-panel {
  padding: 8px 10px 10px;
  color: #f4f7fa;
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
}

.speaker-control-panel--disabled {
  background: rgba(255, 255, 255, 0.02);
}

.speaker-control-panel__header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;

  > div {
    min-width: 0;
  }

  strong,
  small {
    display: block;
  }

  strong {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 12px;
    font-weight: 650;
  }

  strong :deep(.anticon) {
    color: #00df89;
    font-size: 15px;
  }

  small {
    margin-top: 1px;
    overflow: hidden;
    color: #8fa0ac;
    font-size: 10px;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.speaker-control-panel__state {
  display: inline-flex;
  flex: 0 0 auto;
  align-items: center;
  height: 20px;
  padding: 0 8px;
  color: #b8bec5;
  font-size: 10px;
  font-weight: 600;
  white-space: nowrap;
  background: rgba(255, 255, 255, 0.06);
  border: 1px solid rgba(255, 255, 255, 0.14);
  border-radius: 11px;

  &--ready {
    color: #3fe6a0;
    background: rgba(0, 224, 136, 0.12);
    border-color: rgba(0, 224, 136, 0.45);
  }

  &--active {
    color: #7cc4ff;
    background: rgba(30, 142, 245, 0.16);
    border-color: rgba(30, 142, 245, 0.55);
  }

  &--danger {
    color: #ff8a8a;
    background: rgba(229, 50, 45, 0.16);
    border-color: rgba(255, 119, 119, 0.55);
  }
}

.speaker-control-panel__notice {
  display: flex;
  align-items: flex-start;
  gap: 6px;
  margin: 8px 0 0;
  padding: 5px 8px;
  color: #e0b064;
  font-size: 10px;
  line-height: 1.5;
  background: rgba(224, 176, 100, 0.1);
  border: 1px solid rgba(224, 176, 100, 0.28);
  border-radius: 7px;
}

.speaker-control-panel__settings {
  display: grid;
  gap: 6px;
  margin-top: 8px;
}

.speaker-control-panel__setting {
  display: flex;
  align-items: center;
  min-width: 0;
  gap: 6px;

  > span {
    flex: 0 0 auto;
    min-width: 58px;
    color: #8fa0ac;
    font-size: 10px;
    white-space: nowrap;
  }

  :deep(.ant-select) {
    width: 104px;
  }

  :deep(.ant-select-selector) {
    height: 24px !important;
    color: #e6edf2 !important;
    background: rgba(255, 255, 255, 0.06) !important;
    border-color: rgba(255, 255, 255, 0.14) !important;
    border-radius: 6px !important;
  }

  :deep(.ant-select-selection-item) {
    font-size: 11px;
    line-height: 22px !important;
  }

  :deep(.ant-select-arrow) {
    color: #9fb0bb;
  }
}

/* Ten preset levels instead of a slider: one click, no fine dragging. */
.speaker-control-panel__volume {
  display: grid;
  grid-template-columns: auto repeat(10, minmax(0, 1fr));
  align-items: center;
  gap: 3px;

  button {
    height: 22px;
    min-width: 0;
    padding: 0;
    color: #c9d4dd;
    font-size: 10px;
    font-weight: 600;
    font-variant-numeric: tabular-nums;
    background: rgba(255, 255, 255, 0.06);
    border: 1px solid rgba(255, 255, 255, 0.1);
    border-radius: 5px;
    cursor: pointer;
    transition: color 120ms ease, background 120ms ease, border-color 120ms ease;
  }

  button:hover:not(:disabled) {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(0, 224, 136, 0.5);
  }

  button:disabled {
    color: #5f6c76;
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
    cursor: not-allowed;
  }

  .speaker-control-panel__volume-level--active {
    color: #04130d;
    background: #00df89;
    border-color: #43f0ad;
  }

  .speaker-control-panel__volume-level--active:disabled {
    color: rgba(4, 19, 13, 0.7);
    background: rgba(0, 224, 136, 0.55);
    border-color: transparent;
  }
}

.speaker-control-panel__volume-label {
  min-width: 58px;
  margin-right: 3px;
  color: #8fa0ac;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.speaker-control-panel__file {
  display: none;
}

.speaker-control-panel__modes {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 3px;
  margin-top: 8px;
  padding: 3px;
  background: rgba(0, 0, 0, 0.35);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 8px;

  button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 24px;
    gap: 5px;
    color: #97a6b1;
    font-size: 11px;
    font-weight: 600;
    background: transparent;
    border: 1px solid transparent;
    border-radius: 6px;
    cursor: pointer;
    transition: color 120ms ease, background 120ms ease;
  }

  button:hover:not(:disabled) {
    color: #e6fff5;
    background: rgba(255, 255, 255, 0.06);
  }

  button:disabled {
    color: #5f6c76;
    cursor: not-allowed;
  }

  .speaker-control-panel__mode--active {
    color: #04130d !important;
    background: #00df89 !important;
    border-color: #43f0ad !important;
    box-shadow: 0 4px 12px rgba(0, 224, 136, 0.3);
  }
}

/* One row of actions: primary spans two columns, 停止 / 重播 one each; while
 * recording the four buttons share the row. */
.speaker-control-panel__actions {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 5px;
  margin-top: 8px;

  .speaker-control-panel__primary {
    grid-column: span 2;
  }

  :deep(.ant-btn) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 26px;
    gap: 5px;
    padding: 0 8px;
    overflow: hidden;
    color: #eef3f7;
    font-size: 11px;
    font-weight: 600;
    text-overflow: ellipsis;
    white-space: nowrap;
    background: rgba(255, 255, 255, 0.06);
    border-color: rgba(255, 255, 255, 0.12);
    border-radius: 6px;
    box-shadow: none;
  }

  :deep(.ant-btn:hover:not([disabled])) {
    color: #fff;
    background: rgba(255, 255, 255, 0.12);
    border-color: rgba(0, 224, 136, 0.5);
  }

  :deep(.ant-btn-primary) {
    background: #1e8ef5;
    border-color: #5ab0ff;
  }

  :deep(.ant-btn-primary:hover:not([disabled])) {
    background: #3b9ffb;
    border-color: #7cc4ff;
  }

  :deep(.ant-btn-dangerous) {
    color: #fff;
    background: rgba(229, 50, 45, 0.28);
    border-color: rgba(255, 107, 107, 0.6);
  }

  :deep(.ant-btn-dangerous:hover:not([disabled])) {
    background: rgba(229, 50, 45, 0.45);
    border-color: #ff8a8a;
  }

  :deep(.ant-btn[disabled]),
  :deep(.ant-btn[disabled]:hover) {
    color: #5f6c76;
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(255, 255, 255, 0.06);
  }
}

.speaker-control-panel__actions--four .speaker-control-panel__primary {
  grid-column: auto;
}

.speaker-live-toggle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  gap: 6px;
  padding: 0 10px;
  color: #fff;
  font-size: 11px;
  font-weight: 650;
  white-space: nowrap;
  background: #1e8ef5;
  border: 1px solid #5ab0ff;
  border-radius: 6px;
  cursor: pointer;
  transition: background 120ms ease, border-color 120ms ease, box-shadow 120ms ease;
}

.speaker-live-toggle:hover:not(:disabled) {
  background: #3b9ffb;
}

.speaker-live-toggle--active {
  background: linear-gradient(180deg, #e63b36, #c51c24);
  border-color: #ff6b6b;
  box-shadow: 0 0 14px rgba(229, 50, 45, 0.5);
  animation: speaker-live-pulse 1.4s ease-in-out infinite;
}

.speaker-live-toggle--active:hover:not(:disabled) {
  background: linear-gradient(180deg, #ff4d47, #d8202a);
}

.speaker-live-toggle:disabled {
  color: #7d8891;
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.08);
  box-shadow: none;
  cursor: not-allowed;
  animation: none;
}

.speaker-control-panel__live-line {
  display: grid;
  grid-template-columns: 120px minmax(0, 1fr);
  align-items: center;
  gap: 10px;
  margin-top: 6px;
}

.speaker-live-meter {
  display: grid;
  grid-template-columns: repeat(14, minmax(0, 1fr));
  height: 10px;
  gap: 2px;

  span {
    align-self: end;
    height: 100%;
    background: rgba(255, 255, 255, 0.1);
    border-radius: 2px;
    transition: background 80ms ease;
  }

  .speaker-live-meter__bar--on {
    background: #00df89;
  }

  span:nth-child(n + 11).speaker-live-meter__bar--on {
    background: #ffb341;
  }

  span:nth-child(n + 13).speaker-live-meter__bar--on {
    background: #ff5d5d;
  }
}

.speaker-live-status {
  overflow: hidden;
  color: #8fa0ac;
  font-size: 10px;
  font-variant-numeric: tabular-nums;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.speaker-live-status--active {
  color: #d9e3ea;
}

@keyframes speaker-live-pulse {
  0%,
  100% {
    box-shadow: 0 0 14px rgba(229, 50, 45, 0.5);
  }

  50% {
    box-shadow: 0 0 22px rgba(229, 50, 45, 0.75);
  }
}

.speaker-control-panel__recording {
  display: flex;
  align-items: center;
  margin-top: 8px;
  color: #ff9494;
  font-size: 11px;
}

.speaker-control-panel__recording-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  margin-right: 6px;
  background: #ff5d5d;
  border-radius: 50%;
  box-shadow: 0 0 8px rgba(255, 93, 93, 0.8);
  animation: speaker-recording-pulse 1s ease-in-out infinite;
}

.speaker-control-panel__status-line {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  color: #b7c3cd;
  font-size: 10px;
}

.speaker-control-panel__status-text {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.speaker-control-panel__status-percent {
  flex: 0 0 auto;
  color: #3fe6a0;
  font-variant-numeric: tabular-nums;
}

.speaker-control-panel__bar {
  margin-top: 2px;
  line-height: 1;

  :deep(.ant-progress-outer) {
    display: block;
  }

  :deep(.ant-progress-inner) {
    background: rgba(255, 255, 255, 0.1);
  }

  :deep(.ant-progress-bg) {
    background: #00df89;
  }
}

@keyframes speaker-recording-pulse {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.35; }
}
</style>
